import React from 'react';
import {useParams} from 'react-router';
import {useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import BackLink from '../../../ui/BackLink';
import FormTitle from '../../../ui/FormTitle';
import Preloader from '../../../ui/Preloader';
import {EmailNotificationTemplate, LicenseType} from '../../../schema';
import Form from '../../../components/licenseRenewalTemplates/Form';
import {reverse} from '../../../urls';
import BackForward from '../../../components/licenseType/BackForward';
import Menu from '../../../components/licenseType/Menu';
import FormWrapper from '../../../components/licenseType/FormWrapper';

interface PathParams {
  licenseTypeId: string
}

interface RenewalTemplatesQuery {
  licenseType: LicenseType
  templates: EmailNotificationTemplate[]
}

export default function EditScene() {
  const {licenseTypeId} = useParams<PathParams>();
  const {loading, data, error} = useQuery<RenewalTemplatesQuery>(query, {
    variables: {licenseTypeId},
    errorPolicy: 'all'
  });
  const agreementLink = data?.licenseType.agreement ?
    {
      url: reverse('license_agreement_edit', {licenseTypeId, id: data?.licenseType.agreement.id}),
      label: 'Edit Agreement'
    } :
    {url: reverse('license_agreement_add', {licenseTypeId}), label: 'Add Agreement'}

  return (
    <>
      <ReactDocumentTitle title="Edit renewal templates"/>
      <AppLayout className="bg-gray-light">
        <BackLink backLink={reverse('brand_settings_license_types')} title="Back to settings"/>
        {error && <Preloader/>}
        {loading && <Preloader/>}
        {!loading && !error && data &&
          <>
            <Menu activeIndex={2}/>
            <FormWrapper>
              <FormTitle>Renewal Notifications for {data.licenseType?.name}</FormTitle>
              <Form templates={data?.templates} licenseTypeId={licenseTypeId}/>
              <BackForward back={{url: reverse('license_type_edit', {id: licenseTypeId}), label: 'Edit License Type'}}
                           forward={agreementLink}/>
            </FormWrapper>
          </>
        }
      </AppLayout>
    </>
  );
}

const query = gql`
  query EditRenewalTemplatesQuery($licenseTypeId: ID!) {
    licenseType: licenseType(id: $licenseTypeId) {
      id
      name
      agreement {
        id
      }
    }
    templates: emailTemplates(objectId: $licenseTypeId, objectType: license_type) {
      id
      name
      subject
      body
      conditions {
        event
        when {
          units
          unitType
          beforeAfter
        }
      }
    }
  }
`;
